// Global Instructions Rule Applied!
//
// Committed-vs-booked rollup on the deal form's Execution tab (Phase 3 —
// transactions bridge). One row per fund: committed (deal_commitments) vs
// booked / pending asset-transaction amounts linked to the deal, with the
// remaining-to-book delta. Data comes from view__deal_execution_context's
// commitment_rollup key; transactions without a fund surface as "Unassigned".
import React from 'react'
import { Table } from 'antd'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { DealExecutionCommitmentRollupRow } from '../../../state_manager/data_models/deal_execution_board_model.ts'

interface CommittedVsBookedCardProps {
  rollup: DealExecutionCommitmentRollupRow[]
  currencySymbol: string | null | undefined
  className?: string
}

// Accounting amounts show either no decimals or exactly two.
function money(symbol: string | null | undefined, value: number | null | undefined): string {
  if (value == null) return '—'
  const n = Number(value)
  const digits = Number.isInteger(n) ? 0 : 2
  return `${symbol || ''}${n.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`
}

export default function CommittedVsBookedCard({
  rollup,
  currencySymbol,
  className = 'mt-4'
}: CommittedVsBookedCardProps): React.ReactElement {
  const totals = rollup.reduce<{ committed: number; booked: number; pending: number }>(
    (acc, r) => ({
      committed: acc.committed + (r.committed ?? 0),
      booked: acc.booked + (r.booked ?? 0),
      pending: acc.pending + (r.pending ?? 0)
    }),
    { committed: 0, booked: 0, pending: 0 }
  )

  return (
    <FormSection title='Committed vs Booked' titleColor='indigo' scrollable={false} className={className}>
      {rollup.length === 0 ? (
        <div className='text-sm text-gray-400 py-2'>
          No fund commitments or linked transactions on this deal yet.
        </div>
      ) : (
        <Table
          size='small'
          rowKey={(r) => r.funds__id ?? 'unassigned'}
          pagination={false}
          dataSource={rollup}
          columns={[
            {
              title: 'Fund',
              dataIndex: 'funds__name',
              render: (v) => v || <span className='text-gray-400'>Unassigned</span>
            },
            {
              title: 'Committed',
              dataIndex: 'committed',
              align: 'right' as const,
              render: (v) => money(currencySymbol, v)
            },
            {
              title: 'Booked',
              dataIndex: 'booked',
              align: 'right' as const,
              render: (v) => money(currencySymbol, v)
            },
            {
              title: 'Pending',
              dataIndex: 'pending',
              align: 'right' as const,
              render: (v) => money(currencySymbol, v)
            },
            {
              title: 'Remaining',
              key: 'remaining',
              align: 'right' as const,
              render: (_, r) => {
                const remaining = (r.committed ?? 0) - (r.booked ?? 0)
                return (
                  <span className={remaining > 0 ? 'text-amber-600' : remaining < 0 ? 'text-red-500' : 'text-green-600'}>
                    {money(currencySymbol, remaining)}
                  </span>
                )
              }
            }
          ]}
          summary={() =>
            rollup.length > 1 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <span className='font-medium'>Total</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align='right'>
                  <span className='font-medium'>{money(currencySymbol, totals.committed)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align='right'>
                  <span className='font-medium'>{money(currencySymbol, totals.booked)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align='right'>
                  <span className='font-medium'>{money(currencySymbol, totals.pending)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align='right'>
                  <span className='font-medium'>
                    {money(currencySymbol, totals.committed - totals.booked)}
                  </span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      )}
    </FormSection>
  )
}
